import React, { useRef, useState, useEffect } from 'react';
import { PencilSimple, TextT, ArrowCounterClockwise } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';

/**
 * SignaturePad — dual-mode signature capture.
 *  - Drawn: HTML5 canvas with mouse/touch input → base64 PNG
 *  - Typed: text input rendered in script font → plain string
 *
 * Props:
 *  - onChange({ type: 'drawn' | 'typed', data: string }) — fires on every change
 *  - defaultName?: prefill for typed mode
 *  - height?: canvas height (default 180)
 */
export default function SignaturePad({ onChange, defaultName = '', height = 180 }) {
  const [mode, setMode] = useState('drawn');
  const [typed, setTyped] = useState(defaultName);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  const emit = (type, data) => {
    onChange?.({ type, data });
  };

  const getCtx = () => {
    const c = canvasRef.current;
    if (!c) return null;
    return c.getContext('2d');
  };

  // Resize canvas to its container's pixel-perfect width
  useEffect(() => {
    if (mode !== 'drawn') return;
    const c = canvasRef.current;
    if (!c) return;
    const parent = c.parentElement;
    if (!parent) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = parent.getBoundingClientRect();
    c.width = rect.width * dpr;
    c.height = height * dpr;
    c.style.width = `${rect.width}px`;
    c.style.height = `${height}px`;
    const ctx = c.getContext('2d');
    if (ctx) {
      ctx.scale(dpr, dpr);
      ctx.lineWidth = 2.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#0a0a0a';
    }
  }, [mode, height]);

  const pointerXY = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const point = e.touches?.[0] || e;
    return { x: point.clientX - rect.left, y: point.clientY - rect.top };
  };

  const onStart = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = pointerXY(e);
  };

  const onMove = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = getCtx();
    if (!ctx) return;
    const cur = pointerXY(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(cur.x, cur.y);
    ctx.stroke();
    last.current = cur;
    hasInk.current = true;
  };

  const onEnd = () => {
    if (!drawing.current) return;
    drawing.current = false;
    const c = canvasRef.current;
    if (!c) return;
    if (hasInk.current) {
      emit('drawn', c.toDataURL('image/png'));
    }
  };

  const clearCanvas = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, c.width, c.height);
    hasInk.current = false;
    emit('drawn', '');
  };

  const handleTyped = (val) => {
    setTyped(val);
    emit('typed', val);
  };

  return (
    <div className="space-y-3" data-testid="signature-pad">
      {/* Mode tabs */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => { setMode('drawn'); emit('drawn', ''); }}
          className={`flex items-center gap-2 h-10 px-4 font-bold text-sm rounded-lg border-2 border-black transition-all ${
            mode === 'drawn'
              ? 'bg-[#2B4C6F] text-white shadow-[2px_2px_0px_#000]'
              : 'bg-white text-neutral-700 hover:bg-neutral-50'
          }`}
          data-testid="sig-mode-drawn"
        >
          <PencilSimple size={16} weight="bold" />
          Draw
        </button>
        <button
          type="button"
          onClick={() => { setMode('typed'); emit('typed', typed); }}
          className={`flex items-center gap-2 h-10 px-4 font-bold text-sm rounded-lg border-2 border-black transition-all ${
            mode === 'typed'
              ? 'bg-[#2B4C6F] text-white shadow-[2px_2px_0px_#000]'
              : 'bg-white text-neutral-700 hover:bg-neutral-50'
          }`}
          data-testid="sig-mode-typed"
        >
          <TextT size={16} weight="bold" />
          Type
        </button>
      </div>

      {/* Drawn mode */}
      {mode === 'drawn' && (
        <div className="relative border-2 border-black rounded-lg bg-white overflow-hidden">
          <canvas
            ref={canvasRef}
            onMouseDown={onStart}
            onMouseMove={onMove}
            onMouseUp={onEnd}
            onMouseLeave={onEnd}
            onTouchStart={onStart}
            onTouchMove={onMove}
            onTouchEnd={onEnd}
            className="cursor-crosshair touch-none block"
            style={{ height: `${height}px`, width: '100%' }}
            data-testid="sig-canvas"
          />
          {/* Dashed baseline */}
          <div className="pointer-events-none absolute left-4 right-4 border-b-2 border-dashed border-neutral-300" style={{ bottom: '36px' }} />
          <div className="absolute bottom-2 left-3 text-[10px] uppercase tracking-widest font-bold text-neutral-400">
            X — Sign above the line
          </div>
          <button
            type="button"
            onClick={clearCanvas}
            className="absolute top-2 right-2 flex items-center gap-1 h-8 px-3 text-xs font-bold bg-white border-2 border-black rounded shadow-[2px_2px_0px_#000] hover:shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none"
            data-testid="sig-clear"
          >
            <ArrowCounterClockwise size={12} weight="bold" />
            Clear
          </button>
        </div>
      )}

      {/* Typed mode */}
      {mode === 'typed' && (
        <div className="border-2 border-black rounded-lg bg-white p-4 space-y-3">
          <Input
            placeholder="Type your full legal name"
            value={typed}
            onChange={(e) => handleTyped(e.target.value)}
            className="h-12 text-base"
            data-testid="sig-typed-input"
            autoFocus
          />
          <div
            className="h-24 flex items-center justify-center bg-neutral-50 border-2 border-dashed border-neutral-300 rounded"
            data-testid="sig-typed-preview"
          >
            {typed.trim() ? (
              <span
                className="text-3xl text-neutral-900"
                style={{ fontFamily: '"Dancing Script", "Brush Script MT", cursive' }}
              >
                {typed}
              </span>
            ) : (
              <span className="text-sm text-neutral-400 italic">Preview will appear here</span>
            )}
          </div>
          <p className="text-[10px] uppercase tracking-widest font-bold text-neutral-400">
            By typing your name you agree it constitutes your legal electronic signature.
          </p>
        </div>
      )}
    </div>
  );
}
